'use client'

import { getRemoteCaretsOnLeaf, getRemoteCursorsOnLeaf } from '@slate-yjs/react'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from 'app/_components/Popover'
import { useCallback } from 'react'
import { type Text } from 'slate'
import { type RenderLeafProps, useSlateStatic } from 'slate-react'
import type { CustomEditor } from './custom-types'
import Suggestions from './extensions/withMentions/Suggestions'
import UploadPlaceholder from './extensions/withUploads/UploadPlaceholder'
import { type CursorData } from './extensions/withYjs'

export function useLeafRenderer() {
  return useCallback((props: RenderLeafProps) => <Leaf {...props} />, [])
}

export default function Leaf({ attributes, children, leaf }: RenderLeafProps) {
  const editor = useSlateStatic() as CustomEditor

  if (leaf.bold) {
    children = <strong>{children}</strong>
  }

  if (leaf.italic) {
    children = <em>{children}</em>
  }

  if (leaf.mentionQuery) {
    children = (
      <Popover open={true}>
        <PopoverAnchor asChild>
          <span>{children}</span>
        </PopoverAnchor>
        <PopoverContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          align="start"
          className="bg-white"
        >
          <Suggestions
            query={leaf.text.slice(1)}
            suggestionsFetcherAction={editor.suggestionsFetcher}
          />
        </PopoverContent>
      </Popover>
    )
  }

  if (leaf.color) {
    children = <span style={{ color: leaf.color }}>{children}</span>
  }

  getRemoteCursorsOnLeaf<CursorData, Text>(leaf).forEach((cursor) => {
    if (cursor.data) {
      children = (
        <span style={{ backgroundColor: addAlpha(cursor.data.color, 0.2) }}>
          {children}
        </span>
      )
    }
  })

  getRemoteCaretsOnLeaf<CursorData, Text>(leaf).forEach((caret) => {
    if (caret.data) {
      children = (
        // adding the id to later identify the caret in the DOM and remove it when copying the text
        <span className="relative" id={caret.clientId.toString()}>
          <span
            contentEditable={false}
            className="absolute top-0 bottom-0 left-[-1px] w-0.5"
            style={{ backgroundColor: caret.data.color }}
          />
          <span
            contentEditable={false}
            className="absolute top-0 left-[-1px] rounded-r-xl py-1 pr-2 pl-1 text-xs whitespace-nowrap select-none"
            style={{
              backgroundColor: caret.data.color,
              transform: 'translateY(-100%)',
            }}
          >
            {caret.data.name}
          </span>
          {children}
        </span>
      )
    }
  })

  if (leaf.pendingUploads) {
    children = (
      <>
        {leaf.pendingUploads.map((upload) => (
          <UploadPlaceholder key={upload.id} upload={upload} />
        ))}
        {children}
      </>
    )
  }

  if (leaf.glossaryTerm) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <span
            {...attributes}
            className="cursor-help underline decoration-dotted"
            onMouseEnter={(e) => e.currentTarget.click()}
            onTouchStart={(e) => e.currentTarget.click()}
            onMouseLeave={(e) => e.currentTarget.click()}
          >
            {children}
          </span>
        </PopoverTrigger>
        <PopoverContent className="bg-gray-10 p-2" side="top" align="center">
          {leaf.glossaryTerm.explanation}
        </PopoverContent>
      </Popover>
    )
  }

  return <span {...attributes}>{children}</span>
}

function addAlpha(hexColor: string, opacity: number): string {
  const normalized = Math.round(Math.min(Math.max(opacity, 0), 1) * 255)
  return hexColor + normalized.toString(16).toUpperCase()
}
